import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME,
  DEFAULT_SITE_DESCRIPTION,
  buildPageTitle,
  buildPublicPageUrl,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  buildWebPageJsonLd,
  truncateMetaDescription,
} from "../lib/pageSeo";
import { resolveSocialPreview } from "../lib/socialPreview";

export default function PageSeo({
  title,
  description = DEFAULT_SITE_DESCRIPTION,
  path,
  canonicalPath,
  type = "website",
  schemaType,
  noindex = false,
  imagePath,
  imageAlt,
  imageWidth,
  imageHeight,
  imageType,
  articlePublishedTime,
  articleModifiedTime,
  keywords,
  jsonLd,
}) {
  const location = useLocation();
  const resolvedPath = path ?? `${location.pathname}${location.search}`;
  const canonical = buildPublicPageUrl((canonicalPath ?? resolvedPath).split("?")[0]);
  const pageTitle = buildPageTitle(title);
  const metaDescription = truncateMetaDescription(description);
  const socialPreview = resolveSocialPreview({
    path: resolvedPath,
    canonicalPath,
    title: pageTitle,
    imagePath,
    imageAlt,
    imageWidth,
    imageHeight,
    imageType,
  });
  const { imageUrl, imageAlt: ogImageAlt, imageWidth: ogImageWidth, imageHeight: ogImageHeight, imageType: ogImageType } =
    socialPreview;
  const robots = noindex ? "noindex, nofollow" : "index, follow";

  const structuredEntries = [];
  if (!noindex) {
    structuredEntries.push(buildOrganizationJsonLd());
  }

  if (schemaType === "article" && title) {
    structuredEntries.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description: metaDescription,
      url: canonical,
      mainEntityOfPage: canonical,
      image: [imageUrl],
      inLanguage: "en-GB",
      author: {
        "@type": "Organization",
        name: SITE_NAME,
        url: buildPublicPageUrl("/"),
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: buildPublicPageUrl("/"),
        logo: {
          "@type": "ImageObject",
          url: buildPublicPageUrl("/jobuwin-icon.svg"),
        },
      },
      ...(articlePublishedTime ? { datePublished: articlePublishedTime } : {}),
      ...(articleModifiedTime ? { dateModified: articleModifiedTime } : {}),
    });
  } else if (schemaType === "website") {
    structuredEntries.push(buildWebSiteJsonLd(metaDescription));
    const pathOnly = (canonicalPath ?? resolvedPath).split("?")[0];
    if (pathOnly && pathOnly !== "/") {
      structuredEntries.push(
        buildWebPageJsonLd({
          title,
          description: metaDescription,
          url: canonical,
        })
      );
    }
  }

  if (jsonLd) {
    structuredEntries.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));
  }

  return (
    <Helmet>
      <html lang="en-GB" />
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonical} />
      <link rel="sitemap" type="application/xml" title="Sitemap" href={buildPublicPageUrl("/sitemap.xml")} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:type" content={ogImageType} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:alt" content={ogImageAlt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {structuredEntries.map((entry, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}
